import kuyaImage from "../assets/projects/kuya-marketplace.png";
import revoraImage from "../assets/projects/revora-growth.png";
import heroImage from "../assets/portraits/confident-office.png";
import { projectNotesData } from "./projectNotesData";

const editorialPosts = [
  {
    slug: "building-kuya-with-purpose",
    date: "2026-08-18",
    readingTime: 5,
    categories: ["Projects", "Development"],
    image: kuyaImage,
    imageAlt: {
      en: "Kuya marketplace project preview",
      fr: "Aperçu du projet de marketplace Kuya",
    },
    relatedProject: { name: "Kuya MarketPlace", href: "/#projects" },
    title: {
      en: "Building Kuya with a clear product story",
      fr: "Construire Kuya autour d'une vision produit claire",
    },
    excerpt: {
      en: "How I am turning a marketplace project into a case study that explains the decisions behind the interface.",
      fr: "Comment je transforme un projet de marketplace en étude de cas qui explique les décisions derrière l'interface.",
    },
    content: {
      en: [
        {
          type: "lead",
          text: "Kuya is a marketplace project. Building it taught me that a strong portfolio should explain the thinking behind a product, not only display a screenshot.",
        },
        { type: "heading", text: "Start with the problem" },
        {
          type: "paragraph",
          text: "A project page needs enough context for someone to understand what the product is, what I contributed, and where they can explore it. I am keeping those claims grounded in the work that can be demonstrated publicly.",
        },
        { type: "heading", text: "Connect the build to the learning" },
        {
          type: "paragraph",
          text: "The project and this article now point to each other. That small connection turns a static portfolio card into an evolving record of technical decisions, challenges, and lessons.",
        },
        {
          type: "quote",
          text: "A useful case study shows both the result and the reasoning that produced it.",
        },
      ],
      fr: [
        {
          type: "lead",
          text: "Kuya est un projet de marketplace. Sa création m'a appris qu'un bon portfolio doit expliquer la réflexion derrière un produit, et pas seulement afficher une capture d'écran.",
        },
        { type: "heading", text: "Commencer par le problème" },
        {
          type: "paragraph",
          text: "Une page projet doit donner assez de contexte pour comprendre le produit, ma contribution et la façon de l'explorer. Je limite volontairement les affirmations à ce que le travail public permet de démontrer.",
        },
        { type: "heading", text: "Relier la création à l'apprentissage" },
        {
          type: "paragraph",
          text: "Le projet et cet article renvoient maintenant l'un vers l'autre. Ce lien transforme une carte statique en trace évolutive des décisions, défis et enseignements techniques.",
        },
        {
          type: "quote",
          text: "Une étude de cas utile présente le résultat et le raisonnement qui l'a produit.",
        },
      ],
    },
  },
  {
    slug: "bilingual-themes-with-css-variables",
    date: "2026-08-22",
    readingTime: 6,
    categories: ["Development", "Learning"],
    image: revoraImage,
    imageAlt: {
      en: "Developer project interface preview",
      fr: "Aperçu d'une interface de projet",
    },
    title: {
      en: "Bilingual themes without a heavy framework",
      fr: "Des thèmes bilingues sans framework lourd",
    },
    excerpt: {
      en: "A practical approach to adding English, French, light, and dark modes to an existing React portfolio.",
      fr: "Une approche pratique pour ajouter l'anglais, le français et des modes clair et sombre à un portfolio React existant.",
    },
    content: {
      en: [
        {
          type: "lead",
          text: "The goal was to make the portfolio bilingual and theme-aware while preserving its visual identity. The simplest maintainable solution was shared React state plus semantic colour variables.",
        },
        { type: "heading", text: "Keep one source of truth" },
        {
          type: "paragraph",
          text: "A small settings provider owns the current language and theme. Components read translated copy from the same place, and the choices are saved for the next visit.",
        },
        {
          type: "code",
          language: "jsx",
          code: "const value = useMemo(() => ({\n  language,\n  theme,\n  copy: translations[language]\n}), [language, theme]);",
        },
        { type: "heading", text: "Use colours by purpose" },
        {
          type: "paragraph",
          text: "Instead of rewriting every component, each theme changes shared colour variables. Naming colours by their purpose keeps text readable when backgrounds change.",
        },
        {
          type: "code",
          language: "css",
          code: ':root[data-theme="dark"] {\n  --color-background: #012b46;\n  --color-surface: #063b5d;\n  --color-text: #f7f6f5;\n}',
        },
      ],
      fr: [
        {
          type: "lead",
          text: "L'objectif était de rendre le portfolio bilingue et adaptable au thème tout en conservant son identité visuelle. La solution la plus simple repose sur un état React partagé et des variables de couleur sémantiques.",
        },
        { type: "heading", text: "Garder une source unique" },
        {
          type: "paragraph",
          text: "Un petit fournisseur de réglages conserve la langue et le thème. Les composants lisent leurs traductions au même endroit et les choix sont enregistrés pour la prochaine visite.",
        },
        {
          type: "code",
          language: "jsx",
          code: "const value = useMemo(() => ({\n  language,\n  theme,\n  copy: translations[language]\n}), [language, theme]);",
        },
        { type: "heading", text: "Nommer les couleurs par usage" },
        {
          type: "paragraph",
          text: "Au lieu de réécrire chaque composant, chaque thème modifie des variables partagées. Des noms basés sur l'usage gardent le texte lisible lorsque l'arrière-plan change.",
        },
        {
          type: "code",
          language: "css",
          code: ':root[data-theme="dark"] {\n  --color-background: #012b46;\n  --color-surface: #063b5d;\n  --color-text: #f7f6f5;\n}',
        },
      ],
    },
  },
  {
    slug: "growing-a-portfolio-in-small-steps",
    date: "2026-08-24",
    readingTime: 4,
    categories: ["Career", "Notes", "Learning"],
    image: heroImage,
    imageAlt: {
      en: "Israel Kayaba portfolio artwork",
      fr: "Illustration du portfolio d'Israel Kayaba",
    },
    title: {
      en: "Growing a portfolio in small, deliberate steps",
      fr: "Faire évoluer un portfolio par étapes réfléchies",
    },
    excerpt: {
      en: "Why I treat my portfolio as a living developer record instead of rebuilding it whenever my work changes.",
      fr: "Pourquoi je traite mon portfolio comme un journal vivant plutôt que de le reconstruire à chaque évolution.",
    },
    content: {
      en: [
        {
          type: "lead",
          text: "A portfolio is more useful when it grows with the developer. I prefer improving one clear part at a time while preserving the parts that already communicate well.",
        },
        { type: "heading", text: "Preserve what already works" },
        {
          type: "paragraph",
          text: "The navigation, typography, colours, and reusable section headings already form a recognisable visual language. New work should extend that language instead of competing with it.",
        },
        { type: "heading", text: "Write while the decisions are fresh" },
        {
          type: "paragraph",
          text: "Short development notes capture why a decision was made. Over time, those notes become stronger project stories, tutorials, and evidence of how I approach unfamiliar problems.",
        },
        { type: "heading", text: "What I am learning" },
        {
          type: "paragraph",
          text: "The current focus is accessible interface architecture: keeping navigation predictable, content readable, and optional features lightweight.",
        },
      ],
      fr: [
        {
          type: "lead",
          text: "Un portfolio est plus utile lorsqu'il évolue avec le développeur. Je préfère améliorer une partie claire à la fois tout en préservant ce qui communique déjà efficacement.",
        },
        { type: "heading", text: "Préserver ce qui fonctionne" },
        {
          type: "paragraph",
          text: "La navigation, la typographie, les couleurs et les titres réutilisables forment déjà un langage visuel reconnaissable. Les nouveautés doivent prolonger ce langage.",
        },
        {
          type: "heading",
          text: "Écrire pendant que les décisions sont récentes",
        },
        {
          type: "paragraph",
          text: "De courtes notes expliquent pourquoi une décision a été prise. Avec le temps, elles deviennent de meilleures études de cas, des tutoriels et une preuve de ma façon de résoudre les problèmes.",
        },
        { type: "heading", text: "Ce que j'apprends" },
        {
          type: "paragraph",
          text: "Je me concentre actuellement sur l'architecture d'interfaces accessibles : navigation prévisible, contenu lisible et fonctionnalités optionnelles légères.",
        },
      ],
    },
  },
];

export const blogPosts = [...editorialPosts, ...projectNotesData];

export const formatPost = (post, language) => ({
  ...post,
  title: post.title[language],
  excerpt: post.excerpt[language],
  imageAlt: post.imageAlt[language],
  content: post.content[language],
});
